import { useQuery } from '@tanstack/react-query';
import { Link, useParams } from '@tanstack/react-router';
import { ArrowLeft, Ban, CalendarClock, PlayCircle, Route as RouteIcon, Truck, Users } from 'lucide-react';
import { useState, type ReactNode } from 'react';

import { Page, PageHeader } from '@/components/layout/PageHeader';
import { TenantStatusBadge } from '@/components/tenants/TenantStatusBadge';
import { ReactivateTenantDialog, SuspendTenantDialog } from '@/components/tenants/TenantStatusDialogs';
import { Button, buttonVariants } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { FormAlert } from '@/components/ui/form-alert';
import { Skeleton } from '@/components/ui/skeleton';
import { genericErrorMessage, httpStatus } from '@/lib/api-errors';
import { formatDate, formatDateTime, formatNumber } from '@/lib/format';
import { ROLES, tenantKeys, tenantsApi, type Role } from '@/lib/tenants-api';

export const ROLE_LABELS: Record<Role, string> = {
  OWNER: 'Propriétaire',
  ADMIN: 'Administrateur',
  MANAGER: 'Manager',
  DISPATCHER: 'Dispatcher',
  DRIVER: 'Chauffeur',
  ACCOUNTANT: 'Comptable',
  VIEWER: 'Lecteur',
};

const Field = ({ label, children }: { label: string; children: ReactNode }) => (
  <div className="space-y-1">
    <dt className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{label}</dt>
    <dd className="text-sm">{children}</dd>
  </div>
);

const Stat = ({ icon: Icon, label, value, testId }: { icon: typeof Users; label: string; value: ReactNode; testId: string }) => (
  <div className="flex items-center gap-3 rounded-lg border bg-white p-4">
    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-brand-teal/10">
      <Icon className="h-5 w-5 text-brand-teal" aria-hidden />
    </span>
    <div className="min-w-0">
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className="truncate text-lg font-semibold tabular-nums" data-testid={testId}>
        {value}
      </p>
    </div>
  </div>
);

const BackLink = () => (
  <Link to="/tenants" className={buttonVariants({ variant: 'ghost', size: 'sm', className: '-ml-3 text-muted-foreground' })}>
    <ArrowLeft className="mr-1 h-4 w-4" aria-hidden />
    Tenants
  </Link>
);

export const TenantDetailPage = () => {
  const { id } = useParams({ from: '/authed/tenants/$id' });
  const [dialog, setDialog] = useState<'suspend' | 'reactivate' | null>(null);
  const query = useQuery({ queryKey: tenantKeys.detail(id), queryFn: () => tenantsApi.get(id) });

  if (query.isPending) {
    return (
      <Page className="max-w-5xl">
        <BackLink />
        <div className="space-y-4" role="status" aria-label="Chargement du tenant">
          <Skeleton className="h-8 w-64" />
          <Skeleton className="h-40 w-full" />
          <Skeleton className="h-24 w-full" />
        </div>
      </Page>
    );
  }

  if (query.isError) {
    // 400 = id non UUID : même message qu'un tenant inexistant
    const missing = [400, 404].includes(httpStatus(query.error) ?? 0);
    return (
      <Page className="max-w-5xl">
        <BackLink />
        <FormAlert>{missing ? 'Tenant introuvable' : `Impossible de charger le tenant : ${genericErrorMessage(query.error).toLowerCase()}`}</FormAlert>
        {!missing && (
          <Button variant="outline" onClick={() => void query.refetch()}>
            Réessayer
          </Button>
        )}
      </Page>
    );
  }

  const t = query.data;
  const { metrics } = t;
  const usersTotal = ROLES.reduce((sum, r) => sum + metrics.usersByRole[r], 0);
  const canSuspend = t.status === 'ACTIVE' || t.status === 'TRIAL';

  return (
    <Page className="max-w-5xl">
      <BackLink />
      <PageHeader
        title={
          <span className="flex flex-wrap items-center gap-3">
            {t.name}
            <TenantStatusBadge status={t.status} />
          </span>
        }
        description={<span className="font-mono">{t.slug}</span>}
        actions={
          <>
            {canSuspend && (
              <Button variant="destructive" onClick={() => setDialog('suspend')}>
                <Ban className="mr-2 h-4 w-4" aria-hidden />
                Suspendre
              </Button>
            )}
            {t.status === 'SUSPENDED' && (
              <Button className="bg-success text-white hover:bg-success/90" onClick={() => setDialog('reactivate')}>
                <PlayCircle className="mr-2 h-4 w-4" aria-hidden />
                Réactiver
              </Button>
            )}
          </>
        }
      />

      {t.status === 'SUSPENDED' && (
        <Card className="border-danger/30 bg-danger/5" data-testid="suspension-info">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base text-danger">
              <Ban className="h-4 w-4" aria-hidden />
              Tenant suspendu
            </CardTitle>
          </CardHeader>
          <CardContent>
            <dl className="grid gap-4 sm:grid-cols-3">
              <Field label="Suspendu le">{formatDateTime(t.suspendedAt)}</Field>
              <Field label="Par">{t.suspendedBy?.email ?? '—'}</Field>
              <Field label="Motif">{t.suspendedReason ?? '—'}</Field>
            </dl>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Informations</CardTitle>
        </CardHeader>
        <CardContent>
          <dl className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <Field label="Nom">{t.name}</Field>
            <Field label="Slug">
              <span className="font-mono">{t.slug}</span>
            </Field>
            <Field label="Statut">
              <TenantStatusBadge status={t.status} />
            </Field>
            <Field label="Pays">{t.country}</Field>
            <Field label="Fin de l'essai">{formatDate(t.trialEndsAt)}</Field>
            <Field label="Créé le">{formatDateTime(t.createdAt)}</Field>
            <Field label="Email de contact">{t.contactEmail ?? '—'}</Field>
          </dl>
        </CardContent>
      </Card>

      <section className="space-y-3" aria-labelledby="metrics-title">
        <h2 id="metrics-title" className="text-base font-semibold">
          Activité
        </h2>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <Stat icon={Users} label="Utilisateurs" value={formatNumber(usersTotal)} testId="metric-users" />
          <Stat icon={Truck} label="Véhicules" value={formatNumber(metrics.vehiclesCount)} testId="metric-vehicles" />
          <Stat icon={RouteIcon} label="Missions actives" value={formatNumber(metrics.activeMissionsCount)} testId="metric-missions" />
          <Stat icon={CalendarClock} label="Dernière activité" value={formatDateTime(metrics.lastActivityAt)} testId="metric-last-activity" />
        </div>
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Utilisateurs par rôle</CardTitle>
          </CardHeader>
          <CardContent>
            <dl className="grid grid-cols-2 gap-x-6 gap-y-2 sm:grid-cols-4" data-testid="users-by-role">
              {ROLES.map((role) => (
                <div key={role} className="flex items-baseline justify-between gap-2 border-b py-1.5 text-sm">
                  <dt className="text-muted-foreground">{ROLE_LABELS[role]}</dt>
                  <dd className="font-medium tabular-nums">{formatNumber(metrics.usersByRole[role])}</dd>
                </div>
              ))}
            </dl>
          </CardContent>
        </Card>
      </section>

      <SuspendTenantDialog tenant={t} open={dialog === 'suspend'} onOpenChange={(o) => setDialog(o ? 'suspend' : null)} />
      <ReactivateTenantDialog tenant={t} open={dialog === 'reactivate'} onOpenChange={(o) => setDialog(o ? 'reactivate' : null)} />
    </Page>
  );
};
